import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Plus, Copy, Trash2, Facebook } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { bapi, fmtDate } from '@/lib/billingApi';
import { Panel } from './parts';
import { AdLinkDialog, copyText } from './AdLinkDialog';

const Stat = ({ label, value, testId }) => (
  <div className="text-center">
    <div className="text-lg font-black tabular-nums text-slate-900" data-testid={testId}>{value}</div>
    <div className="text-[10px] uppercase tracking-wide text-slate-400">{label}</div>
  </div>
);

const LinkRow = ({ l, i, onDelete }) => (
  <div className="flex flex-col lg:flex-row lg:items-center gap-3 p-4 rounded-xl border border-slate-200" data-testid={`ad-link-row-${i}`}>
    <div className="flex-1 min-w-0">
      <div className="font-semibold text-slate-900 truncate" data-testid={`ad-link-name-${i}`}>{l.name}</div>
      <div className="text-xs text-slate-500 truncate">{l.campaign} · opens {l.destination_label} · made {fmtDate(l.created_at)}</div>
      <div className="text-xs text-slate-400 font-mono truncate mt-1">{l.url}</div>
    </div>
    <div className="grid grid-cols-4 gap-4 lg:w-[300px]">
      <Stat label="Visitors" value={l.visitors} testId={`ad-link-visitors-${i}`} />
      <Stat label="Signups" value={l.signups} testId={`ad-link-signups-${i}`} />
      <Stat label="Signup %" value={`${l.signup_rate}%`} testId={`ad-link-rate-${i}`} />
      <Stat label="Pro" value={l.pro} testId={`ad-link-pro-${i}`} />
    </div>
    <div className="flex gap-2 shrink-0">
      <Button variant="outline" size="sm" onClick={() => copyText(l.url)} data-testid={`ad-link-copy-${i}`}><Copy className="w-4 h-4 mr-1.5" />Copy</Button>
      <Button variant="ghost" size="sm" onClick={() => onDelete(l)} className="text-slate-400 hover:text-red-600" aria-label="Remove link" data-testid={`ad-link-delete-${i}`}><Trash2 className="w-4 h-4" /></Button>
    </div>
  </div>
);

export const AdLinks = () => {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [removing, setRemoving] = useState(null);
  const load = useCallback(() => bapi('/admin/growth/ad-links').then(setData).catch((e) => toast.error(e.message)), []);
  useEffect(() => { load(); }, [load]);

  const remove = async () => {
    try { await bapi(`/admin/growth/ad-links/${removing.id}`, { method: 'DELETE' }); toast.success('Link removed'); load(); } catch (e) { toast.error(e.message); }
    setRemoving(null);
  };

  return (
    <Panel title="Facebook ad links" testId="growth-ad-links">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
        <p className="text-sm text-slate-600 flex items-center gap-2"><Facebook className="w-4 h-4 text-[#1877F2] shrink-0" />One tagged link per ad. Results count from when the link was made.</p>
        <Button onClick={() => setOpen(true)} disabled={!data} className="bg-[#00247D] hover:bg-[#001A5C] text-white" data-testid="ad-link-new-btn">
          <Plus className="w-4 h-4 mr-2" />New ad link
        </Button>
      </div>
      {data && !data.links.length && <p className="text-sm text-slate-400 py-6 text-center" data-testid="ad-links-empty">No ad links yet. Make one for each Facebook ad you run.</p>}
      <div className="space-y-3">{data?.links.map((l, i) => <LinkRow key={l.id} l={l} i={i} onDelete={setRemoving} />)}</div>
      <p className="text-xs text-slate-400 mt-4">Signups are credited to the last ad a visitor clicked before creating their account. Pro counts paid upgrades only.</p>
      <AdLinkDialog open={open} onClose={() => setOpen(false)} destinations={data?.destinations || []} onCreated={load} />
      <AlertDialog open={!!removing} onOpenChange={(o) => !o && setRemoving(null)}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove this ad link?</AlertDialogTitle>
            <AlertDialogDescription>"{removing?.name}" is hidden from this list. Ads already using it keep working, and its visitors and signups stay in your totals.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="ad-link-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={remove} className="bg-red-600 hover:bg-red-700" data-testid="ad-link-delete-confirm">Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Panel>
  );
};
